'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { useToast } from '@/components/ui/use-toast'

import {
  useWebPushNotificationSubscriptionCreateMutation,
  useWebPushNotificationSubscriptionDeleteMutation,
} from '@/hooks/query/notification-subscription'
import {
  getWebPushNotificationSubscriptionIdFromLocalStorage,
  saveWebPushNotificationSubscriptionIdToLocalStorage,
  clearWebPushNotificationSubscriptionIdFromLocalStorage,
  WEB_PUSH_NOTIFICATION_SUBSCRIPTION_STATUS,
} from '@/lib/notification'
import { getClientWebPushNotificationPlatform, getClientUserAgent, urlBase64ToUint8Array } from '@/lib/utils'
import { getWebPushNotificationSubscription } from '@/services/notification-subscription-service'
import { useServiceWorkerStore } from '@/stores/service-worker-store'
import { useWebPushNotificationSubscriptionStore } from '@/stores/web-push-notification-subscription-store'

const NOTIFICATION_PERMISSION_DENIED_ERROR_MESSAGE = 'Notification permission denied.'

type UseWebPushNotificationSubscriptionProps = {
  allowToast?: boolean
}

export function useWebPushNotificationSubscription({
  allowToast = false,
}: UseWebPushNotificationSubscriptionProps = {}) {
  const { isCheckingIfSupported: isCheckingIfServiceWorkerSupported, isSupported: isServiceWorkerSupported } =
    useServiceWorkerStore()
  const {
    setIsChangingSubscription,
    setPermissionState,
    setHasSubscription,
    hasSubscription,
    isChangingSubscription,
    isPermissionGranted,
    isPermissionForPrompt,
  } = useWebPushNotificationSubscriptionStore()
  const { toast } = useToast()
  const tHook = useTranslations('Notification.hooks.use_web_push_notification_subscription')
  const queryClient = useQueryClient()
  const { mutateAsync: createMutateWebPushNotificationSubscription } =
    useWebPushNotificationSubscriptionCreateMutation(queryClient)
  const { mutateAsync: deleteMutateWebPushNotificationSubscription } =
    useWebPushNotificationSubscriptionDeleteMutation(queryClient)

  const checkIfNeedToRenewSubscription = useCallback(async () => {
    if (!isServiceWorkerSupported) return false

    const permission = Notification.permission
    setPermissionState({
      isPermissionForPrompt: permission === 'default',
      isPermissionGranted: permission === 'granted',
    })
    const storedSubscriptionId = getWebPushNotificationSubscriptionIdFromLocalStorage()

    // if permission is not granted or storedSubscriptionId is not available, skip auto-renew
    if (permission !== 'granted' || !storedSubscriptionId) {
      return false
    }

    try {
      // get subscription from BE side
      const subscriptionFromBEPromise = getWebPushNotificationSubscription({ id: storedSubscriptionId })

      // get subscription from service worker side
      const subscriptionFromServiceWorkerPromise = (async () => {
        const registration = await navigator.serviceWorker.ready
        return registration.pushManager.getSubscription()
      })()

      // execute both promises in parallel
      const [beResponse, serviceWorkerResponse] = await Promise.all([
        subscriptionFromBEPromise,
        subscriptionFromServiceWorkerPromise,
      ])

      // renew subscription in these following cases:
      // 1. BE response data status is expired
      // 2. service worker subscription is not available
      // @NOTE: on BE fail, no need to renew because there is a case that the subscription is deleted from other devices
      if (
        (beResponse.data && beResponse.data.status === WEB_PUSH_NOTIFICATION_SUBSCRIPTION_STATUS.EXPIRED) ||
        !serviceWorkerResponse
      ) {
        return true
      }

      // no need to renew subscription since has valid subscription
      setHasSubscription(true)

      return false
    } catch (error) {
      console.error(error)

      // any error should not renew subscription
      return false
    }
  }, [isServiceWorkerSupported, setHasSubscription, setPermissionState])

  const subscribeToWebPushNotification = useCallback(async () => {
    if (!isServiceWorkerSupported) return

    setIsChangingSubscription(true)

    try {
      // wait until service worker is ready
      const registration = await navigator.serviceWorker.ready

      // check for permissions first
      let permission = Notification.permission

      // prompt for permission if default
      const shouldPromptForPermission = permission === 'default'
      if (shouldPromptForPermission) {
        permission = await Notification.requestPermission()
      }

      if (permission !== 'granted') {
        setPermissionState({
          isPermissionForPrompt: false,
          isPermissionGranted: false,
        })
        throw new Error(NOTIFICATION_PERMISSION_DENIED_ERROR_MESSAGE)
      }

      setPermissionState({
        isPermissionForPrompt: false,
        isPermissionGranted: true,
      })

      // subscribe to push notification service
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!),
      })

      // save web push notification subscription to BE
      const createResponse = await createMutateWebPushNotificationSubscription({
        platform: getClientWebPushNotificationPlatform(),
        userAgent: getClientUserAgent(),
        ...sub.toJSON(),
      })

      if (!createResponse.ok || !createResponse.data) {
        throw new Error(`Failed to create web push notification subscription: ${createResponse.message}`)
      }

      // save web push notification subscription id to local storage
      saveWebPushNotificationSubscriptionIdToLocalStorage(createResponse.data._id)
      setHasSubscription(true)
    } catch (error) {
      console.error(error)
      if (allowToast) {
        if (error instanceof Error && error.message === NOTIFICATION_PERMISSION_DENIED_ERROR_MESSAGE) {
          toast({
            variant: 'destructive',
            description: tHook('messages.permission_denied'),
          })
          return
        }
        toast({
          variant: 'destructive',
          description: tHook('messages.subscription_failed'),
        })
      }
    } finally {
      setIsChangingSubscription(false)
    }
  }, [
    allowToast,
    isServiceWorkerSupported,
    setHasSubscription,
    setPermissionState,
    toast,
    createMutateWebPushNotificationSubscription,
  ])

  const unsubscribeToWebPushNotification = useCallback(async () => {
    if (!isServiceWorkerSupported) return

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
      }
      const storedSubscriptionId = getWebPushNotificationSubscriptionIdFromLocalStorage()
      if (storedSubscriptionId) {
        await deleteMutateWebPushNotificationSubscription({ id: storedSubscriptionId })
      }
      clearWebPushNotificationSubscriptionIdFromLocalStorage()
      setHasSubscription(false)
    } catch (error) {
      console.error(error)
      if (allowToast) {
        toast({
          variant: 'destructive',
          description: tHook('messages.unsubscription_failed'),
        })
      }
    }
  }, [allowToast, isServiceWorkerSupported, setHasSubscription, toast, deleteMutateWebPushNotificationSubscription])

  return {
    checkIfNeedToRenewSubscription,
    subscribeToWebPushNotification,
    unsubscribeToWebPushNotification,
    setIsChangingSubscription,
    hasSubscription,
    isChangingSubscription,
    isCheckingIfSupported: isCheckingIfServiceWorkerSupported,
    isSupported: isServiceWorkerSupported,
    isPermissionGranted,
    isPermissionForPrompt,
    isPermissionDenied: !isPermissionForPrompt && !isPermissionGranted,
  }
}
